import { BehaviorSubject } from 'rxjs';
import { createLogger } from './logger';

const logger = createLogger({ module: 'crashHandler' });

interface CrashLog {
  timestamp: string;
  error: Error;
  componentStack?: string;
  userAgent: string;
  url: string;
  lastActions?: string[];
}

// Keep a circular buffer of the last N user actions
const MAX_ACTIONS = 50;
const userActions: string[] = [];

// Create an observable for the crash state
export const crashState = new BehaviorSubject<{ log: CrashLog | null; showFeedback: boolean }>({
  log: null,
  showFeedback: false
});

// Log user actions (navigation, button clicks, etc.)
export const logUserAction = (action: string) => {
  userActions.push(`${new Date().toISOString()}: ${action}`);
  if (userActions.length > MAX_ACTIONS) {
    userActions.shift();
  }
};

// Convert crash data to a log file
export const createCrashLog = (error: Error, componentStack?: string): File => {
  const crashLog: CrashLog = {
    timestamp: new Date().toISOString(),
    error: error,
    componentStack,
    userAgent: navigator.userAgent,
    url: window.location.href,
    lastActions: [...userActions]
  };

  const logContent = [
    '=== Crash Report ===',
    `Timestamp: ${crashLog.timestamp}`,
    `URL: ${crashLog.url}`,
    `User Agent: ${crashLog.userAgent}`,
    '',
    '=== Error Details ===',
    `Name: ${error.name}`,
    `Message: ${error.message}`,
    `Stack: ${error.stack}`,
    '',
    componentStack ? '=== Component Stack ===' : '',
    componentStack || '',
    '',
    '=== Last User Actions ===',
    ...(crashLog.lastActions || []),
    '',
    '=== Environment ===',
    `Screen Size: ${window.innerWidth}x${window.innerHeight}`,
    `DPR: ${window.devicePixelRatio}`,
    `Platform: ${navigator.platform}`,
    `Language: ${navigator.language}`,
  ].join('\n');

  return new File(
    [logContent],
    `crash-${new Date().toISOString().replace(/[:.]/g, '-')}.log`,
    { type: 'text/plain' }
  );
};

// Handle uncaught errors and show feedback modal
export const handleCrash = (error: Error, componentStack?: string) => {
  const log: CrashLog = {
    timestamp: new Date().toISOString(),
    error,
    componentStack,
    userAgent: navigator.userAgent,
    url: window.location.href,
    lastActions: [...userActions]
  };

  // Update crash state to show feedback modal
  crashState.next({ log, showFeedback: true });

  // Log to console for development
  if (import.meta.env.DEV) {
    logger.error('Application crash:', error);
    logger.debug('Crash details:', log);
  }
};

// Initialize crash handlers
export const initCrashHandlers = () => {
  // Handle uncaught errors
  window.onerror = (message, source, lineno, colno, error) => {
    if (error) {
      handleCrash(error);
    }
    return false;
  };

  // Handle unhandled promise rejections
  window.onunhandledrejection = (event) => {
    const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
    handleCrash(error);
  };
}; 